import BifrostButton from "@/Components/BifrostElementComponent/BifrostButton/BifrostButton"
import BifrostModal from "@/Components/BifrostPageComponent/BifrostModal/BifrostModal"
import { useDateTime } from "@/Hooks"
import { DatePicker, Space, Typography } from "@douyinfe/semi-ui"
import * as DateFns from "date-fns"
import { useCallback, useState } from "react"

const DateTimeRangePicker = ({ dateTimePickerHandler, validateInput, validateTimeRange, resetTimeRangeValidation }) => {
	const { isBeforeToday, isBeforeGivenDate, isAfterGivenDate, isValidDateRange, isValidTimeRange } = useDateTime()

	const [startTime, setStartTime] = useState(null)
	const [endTime, setEndTime] = useState(null)
	const [startTimeError, setStartTimeError] = useState(null)
	const [endTimeError, setEndTimeError] = useState(null)

	const BottomSlot = ({ message }) => (
		<Space style={{ padding: "12px 20px" }}>
			<Typography.Paragraph strong type="tertiary" style={{}}>
				{message}
			</Typography.Paragraph>
		</Space>
	)

	const handleStartTimeChange = (date) => {
		setStartTime(date)
		if (endTime) dateTimePickerHandler(date, endTime)
	}

	const handleEndTimeChange = (date) => {
		setEndTime(date)
		if (startTime) dateTimePickerHandler(startTime, date)
	}

	/**
	 * Handle the disabled dates based on the time selection status
	 * @function
	 * @param {Date} date - The Date object to be checked
	 * @param {"start"|"end"} target - The target time selection status:
	 *   - "start": choosing the start time
	 *   - "end": choosing the end time
	 * @returns {boolean} - The disabled status of the date
	 * @description
	 * The function following the senario of activation time and deactivation time selection for Time-based Direct tunnel.
	 * If we need to use the component for other purposes, we need to take the function out of the conmponent and make the disable rules more generic.
	 * For the Time-based Direct tunnel:
	 * - The activation time and deactivation time should not be earlier than the date of the config creation.
	 * - The activation time should not be later than the deactivation time.
	 * - If the user set up the activation time but not set the deactivation time yet,  the available deactivation time should be after the activation time.
	 * - If the user set up the deactivation time but not set the activation time yet, the available activation time should be before the deactivation time.
	 * - If the user set up both activation time and deactivation time, then the user want to modify the time range, if the new activation time or deactivation time is invalid, we need to show the error warning.
	 * @sideeffect
	 * - startTime
	 * - endTime
	 * - startTimeError
	 * - endTimeError
	 */

	const handleDisableDate = useCallback(
		(date, target) => {
			const clearErrors = () => {
				setEndTimeError(null)
				setStartTimeError(null)
			}

			// Case 1: Both dates are empty
			if (!startTime && !endTime) {
				clearErrors()
				return isBeforeToday(date)
			}

			// Case 2: Only startTime is set
			if (startTime && !endTime) {
				setEndTimeError(null)
				return target === "start" ? isBeforeToday(date) : isBeforeGivenDate(date, startTime) // included the day of the startTime.
			}

			// Case 3: Only endTime is set
			if (!startTime && endTime) {
				return target === "end" ? isBeforeToday(date) : isAfterGivenDate(date, endTime) // included the day of the endTime.
			}

			// Case 4: Both dates are set
			const isInvalidRange = !isValidDateRange(startTime, endTime)

			if (target === "start") {
				setEndTimeError(isInvalidRange ? "The startTime should not be after the endTime." : null)
				setStartTimeError(null)
			} else {
				setStartTimeError(isInvalidRange ? "The endTime should not be before the startTime." : null)
				setEndTimeError(null)
			}

			return isBeforeToday(date)
		},
		[startTime, endTime, startTimeError, endTimeError]
	)

	/**
	 * Handle the disabled time based on the time selection status
	 * @function
	 * @param {Date} target - The target time selection status:
	 *   - "start": choosing the start time
	 *   - "end": choosing the end time
	 * @returns {Object| {disableHours: Array, disableMinutes:Array}} - The disabled status of the time
	 *   - disabledHours: The disabled hours
	 *   - disabledMinutes: The disabled minutes
	 * @description
	 * This function is handle the disabled time based on the time selection status.
	 * Because the time range is not allowed to be less than 5 minutes, we need to disable the time that is less than 5 minutes from the other time.
	 * The date was handled by the handleDisableDate function, so we don't need to check the date here.
	 * @sideeffect
	 * - startTime
	 * - endTime
	 * - startTimeError
	 * - endTime
	 */
	const handleDisabledTime = useCallback(
		(target) => {
			const isInvalidTimeRange = !isValidTimeRange(startTime, endTime, (t1, t2, unit) => {
				switch (unit) {
					case "hour":
						return t1 < t2
					case "minute":
						return t1 - t2 < 5 // The deactivation time must be at least 5 minutes after activation
					default:
						return false
				}
			})

			// if the startTime and endTime are not set, we don't need to check the time.
			if (!startTime && !endTime) return null

			// if the time is not at the same day, we don't need to check the time.
			if (!DateFns.isSameDay(startTime, endTime)) return null

			if (target === "start") {
				setEndTimeError(isInvalidTimeRange ? "The startTime should be at least 5 minutes before endTime." : null)
				setStartTimeError(null)
				return {
					disabledHours: () => {
						const hours = []
						for (let i = endTime.getHours() + 1; i <= 23; i++) {
							// +1 for enable the same hour.
							hours.push(i)
						}
						return hours
					},
					disabledMinutes: () => {
						const minutes = []
						for (let i = endTime.getMinutes() - 5; i <= 59; i++) {
							// -5 for disable the gap of 5 minutes.
							minutes.push(i)
						}
						return minutes
					}
				}
			} else {
				setStartTimeError(isInvalidTimeRange ? "The endTime should be at least 5 minutes after startTime." : null)
				setEndTimeError(null)
				return {
					disabledHours: () => {
						const hours = []
						for (let i = 0; i < startTime.getHours(); i++) {
							hours.push(i)
						}
						return hours
					},
					disabledMinutes: () => {
						const minutes = []
						for (let i = 0; i <= startTime.getMinutes() + 5; i++) {
							// +5 for disable the gap of 5 minutes.
							minutes.push(i)
						}
						return minutes
					}
				}
			}
		},
		[startTime, endTime, startTimeError, endTimeError]
	)

	return (
		<>
			<DatePicker
				type="dateTime"
				position="bottom"
				insetLabel={"Activation Time"}
				format="dd-MM-yyyy HH:mm:ss"
				insetInput
				timePickerOpts={{
					scrollItemProps: { mode: "wheel", cycled: true }
				}}
				disabledDate={(date) => handleDisableDate(date, "start")}
				disabledTime={() => handleDisabledTime("start")}
				bottomSlot={<BottomSlot message="Please choose the activation time" />}
				onChange={(date) => handleStartTimeChange(date)}
				weekStartsOn={1}
				validateStatus={validateInput || startTimeError ? "error" : "success"}
			/>
			<DatePicker
				type="dateTime"
				position="bottom"
				insetLabel={"Deactivation Time"}
				format="dd-MM-yyyy HH:mm:ss"
				insetInput
				timePickerOpts={{
					scrollItemProps: { mode: "wheel", cycled: true }
				}}
				disabledDate={(date) => handleDisableDate(date, "end")}
				disabledTime={() => handleDisabledTime("end")}
				bottomSlot={<BottomSlot message="Please choose the deactivation time" />}
				onChange={(date) => handleEndTimeChange(date)}
				weekStartsOn={1}
				validateStatus={validateInput || endTimeError ? "error" : "success"}
			/>
			<BifrostModal
				title={"Invalid time range"}
				open={validateTimeRange}
				modalPage={<p style={{ color: "black" }}>The deactivation time must be at least 5 minutes after activation. Please select a valid time range.</p>}
				modalActions={
					<>
						<BifrostButton style={{ display: "inline-block" }} color="gray" onClick={() => resetTimeRangeValidation()}>
							OK
						</BifrostButton>
					</>
				}
			/>
		</>
	)
}

export default DateTimeRangePicker
